import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { batchAPI } from '../services/api';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { X, Server } from 'lucide-react';

const VendorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sftpInfo, setSftpInfo] = useState(null);
  const [showSftpPanel, setShowSftpPanel] = useState(false);

  useEffect(() => {
    batchAPI.getSftpInfo()
      .then(res => {
        if (res.data?.sftp_enabled) {
          setSftpInfo(res.data);
          setShowSftpPanel(true);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <DashboardLayout user={user} logout={logout}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '800', color: 'hsl(var(--foreground))', marginBottom: '4px' }}>
          Vendor Dashboard
        </h1>
        <p style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
          Upload and manage your image batches
        </p>
      </div>

      {/* Deactivated vendor warning */}
      {user?.is_active === false && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#991b1b' }}>Account Deactivated</div>
            <div style={{ fontSize: '13px', color: '#b91c1c' }}>Your account has been deactivated. You can view existing batches but cannot upload new ones. Please contact your administrator.</div>
          </div>
        </div>
      )}

      {/* SFTP Upload Instructions Panel */}
      {showSftpPanel && sftpInfo && (
        <div style={{
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          border: '1px solid #bae6fd',
          borderRadius: 'var(--radius-lg)',
          padding: '20px 24px',
          marginBottom: '24px',
          position: 'relative'
        }}>
          <button
            onClick={() => setShowSftpPanel(false)}
            style={{
              position: 'absolute', top: '12px', right: '12px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#64748b', padding: '4px'
            }}
          >
            <X size={16} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{
              background: '#0284c7', borderRadius: '8px', padding: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Server size={18} color="white" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0c4a6e' }}>
                SFTP Upload Enabled
              </h3>
              <p style={{ margin: 0, fontSize: '12px', color: '#0369a1' }}>
                Use FileZilla or MobaXterm to upload large batches directly via SFTP
              </p>
            </div>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px', marginBottom: '16px'
          }}>
            {[
              { label: 'Host', value: sftpInfo.host },
              { label: 'Port', value: '22' },
              { label: 'Username', value: sftpInfo.sftp_username },
              { label: 'Auth Type', value: 'Private Key (.pem file)' },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: 'white', borderRadius: '8px', padding: '10px 14px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>
                  {label}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0c4a6e', wordBreak: 'break-all' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: '#0c4a6e', borderRadius: '8px', padding: '14px 18px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#7dd3fc', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Required Folder Structure (after connecting)
            </div>
            <pre style={{
              margin: 0, fontSize: '13px', color: '#e0f2fe', fontFamily: 'monospace', lineHeight: '1.7'
            }}>{`sftp_batches/  ← you land here on connect
└── {job_name}/        e.g. Alabama, Pennsylvania
    └── {batch_name}/
        ├── GroupMetadata.csv  (or .xlsx)
        └── artifacts/
            ├── image001.tif
            └── image002.tif`}</pre>
          </div>

          <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#0369a1' }}>
            Once uploaded, the system will automatically pick up your batch within{' '}
            <strong>1–2 minutes</strong> and begin validation.
          </p>
        </div>
      )}

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--foreground))', marginBottom: '16px' }}>
          Quick Actions
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
          {[
            ...(user?.is_active !== false ? [{ icon: '📁', label: 'Upload New Batch', desc: 'Create and upload a new image batch', onClick: () => navigate('/batch/create') }] : []),
            { icon: '📋', label: 'View Batches', desc: 'Browse, filter and manage all your batches', onClick: () => navigate('/batches') },
          ].map((action) => (
            <div
              key={action.label}
              onClick={action.onClick}
              style={{
                background: 'var(--gradient-card)',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius-lg)',
                padding: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                transition: 'all 0.2s',
                boxShadow: 'var(--shadow-card)',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-card-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                {action.icon}
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'hsl(var(--foreground))', marginBottom: '4px' }}>{action.label}</div>
                <div style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))' }}>{action.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </DashboardLayout>
  );
};

export default VendorDashboard;
